import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

// Axios instance for user/profile API calls
const profileAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token if present
profileAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    else console.warn('No auth token found in storage for profileAPI request');
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 globally
profileAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const BASE_URL = '/users';

// Caching
const CACHE_PREFIX = 'erp_profile_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    const payload = JSON.stringify({ value, expires: Date.now() + ttl });
    sessionStorage.setItem(cacheKey(key), payload);
  } catch (e) {
    // ignore storage errors
  }
}

const memoryCache = new Map();
function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}
function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try { memoryCache.delete(key); } catch (e) { }
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
}


/**
 * Profile service: get current user and update profile
 * - GET /api/v1/users/me
 * - PUT /api/v1/users/me
 * Caching: stored in memory + sessionStorage under key 'me::current'
 */
const PROFILE_KEY = 'me::current';

const profileService = {
  async getProfile(forceRefresh = false) {
    const key = PROFILE_KEY;
    if (!forceRefresh) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) {
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }
    }

    try {
      const response = await profileAPI.get(`${BASE_URL}/me`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateProfile(payload) {
    try {
      const response = await profileAPI.put(`${BASE_URL}/me`, payload);
      const updated = response.data;

      // Update caches with new value
      try {
        writeMemoryCache(PROFILE_KEY, updated);
        writeCache(PROFILE_KEY, updated);
      } catch (e) {
        // ignore cache write errors
      }

      // Also update a generic user_data storage if used elsewhere.
      // Merge into the existing user_data rather than replacing it: the
      // /users/me response may omit fields the app relies on at boot (notably
      // `role`), and overwriting would strip them — leaving the app stuck on
      // "Loading session..." after a refresh because role context can't resolve.
      try {
        const userDataKey = 'user_data';
        const prevRaw = localStorage.getItem(userDataKey) || sessionStorage.getItem(userDataKey);
        const prev = prevRaw ? JSON.parse(prevRaw) : {};
        const merged = { ...prev, ...updated };
        if (!merged.role && prev.role) merged.role = prev.role;
        localStorage.setItem(userDataKey, JSON.stringify(merged));
        sessionStorage.setItem(userDataKey, JSON.stringify(merged));
      } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async resetPassword(payload) {
    try {
      const response = await profileAPI.post('/auth/reset-password', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // Utility to clear cached profile (useful on logout)
  clearCache() {
    deleteCacheEntry(PROFILE_KEY);
  }
};

export default profileService;
